"use client";

import { CiSquareMinus, CiSquarePlus } from "react-icons/ci";
import { useEffect, useState } from "react";
import { homepageFaqs as faqs } from "@/components/Home/faqData";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [visibleFaqs, setVisibleFaqs] = useState(new Set());

  const toggleIndex = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Header animation
          setTimeout(() => setIsHeaderVisible(true), 200);

          // Staggered FAQ animations
          faqs.forEach((_, index) => {
            setTimeout(() => {
              setVisibleFaqs(prev => new Set([...prev, index]));
            }, 400 + (index * 100));
          });
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('faq-section');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <div id="faq-section" className="bg-white dark:bg-slate-950">
      <div className="grid grid-cols-1 gap-4 px-4 py-12 mx-auto lg:grid-cols-2 max-w-7xl sm:px-6 lg:px-8 lg:py-20">
        <div className={`flex flex-col justify-center max-w-2xl mx-auto transition-all duration-800 ease-out ${
          isHeaderVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
        }`}>
          <h2 className="text-5xl font-light tracking-tight text-gray-900 dark:text-white lg:max-w-sm">
            Frequently Asked <span className="text-orange-500">Questions</span>
          </h2>
          <p className="mt-2 text-base font-light text-gray-500 dark:text-slate-400 lg:max-w-md">
            Have questions about Prep5 and the Execution Excellence platform? Explore answers to common queries about how Salesably helps your team execute better.
          </p>
        </div>
        <div className="max-w-2xl mx-auto divide-y divide-gray-200 dark:divide-slate-700">
          <dl className="mt-6 space-y-6 divide-y divide-gray-200 dark:divide-slate-700">
            {faqs.map((faq, index) => (
              <details
                key={faq.question}
                className={`pt-6 transition-all duration-700 ease-out ${
                  visibleFaqs.has(index) 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 translate-x-6'
                }`}
                open={openIndex === index}
              >
                <summary
                  className="flex items-center justify-between max-w-2xl text-lg font-normal text-gray-900 dark:text-slate-100 cursor-pointer"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleIndex(index);
                  }}
                >
                  {faq.question}
                  <span className="flex items-center ml-3 h-7">
                    {openIndex === index ? (
                      <CiSquareMinus
                        className="w-6 h-6 text-gray-400 dark:text-slate-500"
                        aria-hidden="true"
                      />
                    ) : (
                      <CiSquarePlus
                        className="w-6 h-6 text-gray-400 dark:text-slate-500"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </summary>
                <dd className="pr-12 mt-2">
                  <p className="text-base font-light text-gray-500 dark:text-slate-400">
                    {faq.answer}
                  </p>
                </dd>
              </details>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
